import { LoadingOverlay, Stack } from '@mantine/core';
import { useMergedRef, useResizeObserver } from '@mantine/hooks';
import type { ReactFlowInstance } from '@xyflow/react';
import { MiniMap, ReactFlow } from '@xyflow/react';
import { observer } from 'mobx-react-lite';
import { forwardRef, useCallback, useMemo } from 'react';
import {
	useLineageFlowContext,
	useLineageFlowUIContext,
} from '../../context/LineageFlowProvider/LineageFlowContext';
import ActionBar from '../ActionBar/ActionBar';
import Controls from '../Controls/Controls';
import type { LineageFlowEdge } from '../EntityEdge/EntityEdge.types';
import type { LineageFlowNode } from '../EntityNode/EntityNode.types';
import type { LineageFlowTemporaryNode } from '../TemporaryNode/TemporaryNode.types';
import { useStyles } from './LineageFlow.styles';
import type { LineageFlowProps } from './LineageFlow.types';
import { edgeTypes, nodeTypes } from './LineageFlow.types';
import LineageFlowContainer from './LineageFlowContainer';

import '@xyflow/react/dist/base.css';
import { useDeepCompareEffect } from 'ahooks';

// Static props for ReactFlow that never change
const REACT_FLOW_STATIC_PROPS = {
	nodesConnectable: true,
	nodesDraggable: false,
	nodesFocusable: false,
	edgesFocusable: false,
	elementsSelectable: true,
	snapToGrid: false,
	minZoom: 0.25,
	maxZoom: 1.5,
	fitView: true,
	panOnScroll: true,
	onlyRenderVisibleElements: true,
} as const;

const LineageFlow = forwardRef<HTMLDivElement, LineageFlowProps>(
	({ catalogFilter, filters, showActionBar, createAccess = false }, ref) => {
		const [resizeObserverRef, rect] = useResizeObserver();
		const mergedRef = useMergedRef(ref, resizeObserverRef);

		const {
			reactFlowInstance,
			nodes,
			edges,
			setDimensions,
			isLoading,
			onInitReactFlow,
			onFilterChange,
			onNodesChange,
			onEdgesChange,
			onEdgeMouseEnter,
			onEdgeMouseLeave,
		} = useLineageFlowContext();
		const { isFullscreen, setIsFullscreen } = useLineageFlowUIContext();

		const { classes, theme } = useStyles({ isFullscreen });

		// Only update dimensions if rect actually changed (deep comparison)
		useDeepCompareEffect(() => {
			setDimensions(rect);
		}, [rect, setDimensions]);

		// Only call onFilterChange if catalogFilter actually changed (deep comparison)
		useDeepCompareEffect(() => {
			onFilterChange(catalogFilter);
		}, [catalogFilter, onFilterChange]);

		const handleInitReactFlow = useCallback(
			(
				instance: ReactFlowInstance<
					LineageFlowNode | LineageFlowTemporaryNode,
					LineageFlowEdge
				>
			) => {
				onInitReactFlow({
					reactFlowInstance: instance,
					createAccess,
					filter: catalogFilter,
				});
			},
			[catalogFilter, createAccess, onInitReactFlow]
		);

		// Memoize onExitFullscreen to prevent recreation on every render
		const onExitFullscreen = useCallback(() => {
			setIsFullscreen(false);
		}, [setIsFullscreen]);

		// Memoize MiniMap bgColor to avoid recalculation on every render
		const miniMapBgColor = useMemo(
			() => theme.other.getColor('surface/primary/default'),
			[theme]
		);

		// Memoize MiniMap props to prevent recreation on every render
		const miniMapProps = useMemo(
			() => ({
				bgColor: miniMapBgColor,
				position: 'top-right' as const,
				pannable: true,
				zoomable: true,
			}),
			[miniMapBgColor]
		);

		// Memoize ActionBar render to prevent unnecessary re-renders
		const actionBar = useMemo(
			() => (showActionBar ? <ActionBar>{filters}</ActionBar> : null),
			[showActionBar, filters]
		);

		return (
			<LineageFlowContainer
				isFullscreen={isFullscreen}
				exitFullscreen={onExitFullscreen}
			>
				<Stack className={classes.wrapper}>
					{actionBar}
					<ReactFlow
						ref={mergedRef}
						className={classes.reactFlow}
						nodes={nodes}
						edges={edges}
						nodeTypes={nodeTypes}
						edgeTypes={edgeTypes}
						onInit={handleInitReactFlow}
						onNodesChange={onNodesChange}
						onEdgesChange={onEdgesChange}
						onEdgeMouseEnter={onEdgeMouseEnter}
						onEdgeMouseLeave={onEdgeMouseLeave}
						{...REACT_FLOW_STATIC_PROPS}
					>
						{<LoadingOverlay visible={isLoading} />}
						<Controls reactFlowInstance={reactFlowInstance as any} />
						{isFullscreen && <MiniMap {...miniMapProps} />}
					</ReactFlow>
				</Stack>
			</LineageFlowContainer>
		);
	}
);

const ObservedLineageFlow = observer(LineageFlow);

LineageFlow.displayName = 'LineageFlow';
ObservedLineageFlow.displayName = 'ObservedLineageFlow';

export default ObservedLineageFlow;
